import _ from 'lodash'
import isServerOffline from '@/utils/isServerOffline'
import { FETCH_BILLING_SUBSCRIPTION, FETCH_BILLING_CARDS } from './actions'
import config from '@/config/constants'
import store from './index'
import { createMemoizedGetter } from '@/utils/reactiveHelpers'
import { BACKEND_APP_TYPE } from '@/config/appTypes'

export default {
  currentBucket: (state, getters) => {
    return getters.bucketById()
  },
  bucketById: state => id => {
    if (id === undefined && state.route !== undefined) {
      id = state.currentBucketId
    }
    if (id !== undefined && state.buckets[id]) {
      return state.buckets[id]
    }
  },
  currentBucketId: (state, getters) => {
    return state.currentBucketId
  },
  entityArray: state => entity => {
    const entities = state[entity].entities
    if (_.isEmpty(entities)) return []
    return Object.keys(entities).map(key => entities[key])
  },
  aggregableMetrics: (state, getters) => ({ bucketId, appName }) => {
    const bucket = getters.bucketById(bucketId)
    if (!bucket) return []

    let allMetricsNames
    if (appName) {
      const metricsFilter = new Set()
      bucket.processes
        .filter(p => p.name === appName)
        .forEach(p => Object.keys(p.axm_monitor).forEach(name => metricsFilter.add(name)))
      allMetricsNames = Array.from(metricsFilter)
    } else {
      allMetricsNames = Object.keys(bucket.metrics)
    }

    const metrics = allMetricsNames.filter(metric => {
      return bucket.metrics[metric].aggregable === true
    })
    metrics.push('cpu')
    metrics.push('memory')
    return metrics
  },
  preferredMetrics: (state, getters) => ({ bucketId = state.currentBucketId, appName }) => {
    const allMetrics = new Set(getters.aggregableMetrics({ appName }))

    let storedMetrics
    if (state.preferredMetrics) {
      const entry = state.preferredMetrics.find(
        entry => entry.bucketId === bucketId && entry.appName === appName,
      )
      if (entry) storedMetrics = entry.metrics
    }

    if (!storedMetrics) {
      // Default metrics
      storedMetrics = [
        { name: 'cpu', aggregationType: 'avg' },
        { name: 'memory', aggregationType: 'avg' },
        { name: 'HTTP', aggregationType: 'avg' },
        { name: 'HTTP Mean Latency', aggregationType: 'avg' },
        { name: 'pmx:http:latency', aggregationType: 'avg' },
        { name: 'Loop delay', aggregationType: 'avg' },
        { name: 'Event Loop Latency', aggregationType: 'avg' },
      ]
    }

    return storedMetrics.filter(m => allMetrics.has(m.name))
  },
  user: state => {
    return state.user
  },
  plans: state => {
    return state.plans
  },
  bucketPlan: (state, getters) => bucketId => {
    const data = getters.bucketById(bucketId)
    if (!data) return
    const bucket = data.bucket
    const planName =
      bucket.payment && bucket.payment.offer_type
        ? bucket.payment.offer_type
        : bucket.credits.offer_type
    const plan = _.cloneDeep(state.plans[planName])
    if (data.bucket.limitations !== undefined && data.bucket.limitations.processes !== undefined) {
      plan.process_limit = data.bucket.limitations.processes
    }
    return plan
  },
  currentBillingSubscription: createMemoizedGetter(
    () => ({
      bucketId: store.state.currentBucketId,
      isUserAdmin: store.getters.isUserAdmin,
    }),
    ({ bucketId, isUserAdmin }) => {
      if (!bucketId || !isUserAdmin) {
        return Promise.resolve()
      }
      return store.dispatch(FETCH_BILLING_SUBSCRIPTION, { bucketId }).catch(e => {
        /* If there is no subscription, the backend returns a 404 */
        if (e.status === 404) {
          return undefined
        }
        throw e
      })
    },
  ),
  currentPlan: (state, getters) => {
    return getters.bucketPlan(state.currentBucketId)
  },
  isElligibleForTrial: (state, getters) => currentPlanId => {
    if (!currentPlanId && getters.currentPlan) {
      currentPlanId = getters.currentPlan.id
    }
    if (
      !state.user ||
      state.user.trial_started ||
      !state.plans.trial ||
      !state.plans.trial.from.includes(currentPlanId)
    ) {
      return Promise.resolve(false)
    }
    return true
  },
  trialPlans: state => {
    if (!state.plans.trial) return
    const ids = Array.isArray(state.plans.trial.to) ? state.plans.trial.to : [state.plans.trial.to]
    return ids.map(id => state.plans[id])
  },
  defaultTrialPlan: state => {
    if (!state.plans.trial) return
    return state.plans[state.plans.trial.default]
  },
  currentPlanType: (state, getters) => {
    const currentPlan = getters.currentPlan
    if (!currentPlan) {
      return
    }
    if (currentPlan.name === 'enterprise') {
      return 'enterprise'
    }
    if (currentPlan.is_premium) {
      return 'plus'
    }
    return 'free'
  },
  currentBillingCards: state => {
    const currentBucketId = state.currentBucketId
    if (!currentBucketId) return Promise.resolve([])
    return store.dispatch(FETCH_BILLING_CARDS, { bucketId: currentBucketId })
  },
  isDedicated: state => {
    return config.DEDICATED
  },
  hasFeature: (state, getters) => ({ feature }) => {
    const data = getters.currentBucket
    if (!data || !data.bucket) return

    const bucket = data.bucket

    // Bucket overrides
    if (bucket.features && typeof bucket.features[feature] === 'boolean') {
      return bucket.features[feature]
    }

    const planName =
      bucket.payment && bucket.payment.offer_type
        ? bucket.payment.offer_type
        : bucket.credits.offer_type
    const plan = state.plans[planName]

    if (plan.label == 'free_v4') plan.features.notifications = false

    // Plan default values
    if (plan && plan.features && typeof plan.features[feature] === 'boolean') {
      return plan.features[feature]
    }

    // Unknown feature
    return false
  },
  freeFeatures: state => {
    return Object.values(state.plans).find(p => p.type === 'free_v4').features
  },
  plusFeatures: state => {
    return Object.values(state.plans).find(p => p.type === 'plus').features
  },
  enterpriseFeatures: state => {
    return Object.values(state.plans).find(p => p.type === 'enterprise').features
  },
  persistence: (state, getters) => {
    const plan = getters.currentPlan
    const bucketState = getters.currentBucket
    if (!plan || !bucketState || !bucketState.bucket) return 14 * 86400
    return bucketState.bucket.limitations.retention || plan.persistence
  },
  // If user is super pm2.io admin
  isUserSuperAdmin: state => {
    // Allow any user to check while developing
    if (process.env.NODE_ENV === 'development') return true
    return state.user ? state.user.role === 'admin' : false
  },
  // Admin is either Owner or Admin
  isUserAdmin: (state, getters) => {
    if (state.user.id === undefined) return false
    if (getters.currentBucket === undefined) return false
    return (
      state.user.id === getters.currentBucket.bucket._owner._id ||
      state.user.id === getters.currentBucket.bucket._owner.id ||
      !!getters.currentBucket.bucket.roles.admin.filter(user_id => user_id == state.user._id)
    )
  },
  isUserOwner: (state, getters) => {
    if (state.user.id === undefined) return false
    if (getters.currentBucket === undefined) return false
    return (
      state.user.id === getters.currentBucket.bucket._owner._id ||
      state.user.id === getters.currentBucket.bucket._owner.id
    )
  },
  hasSeenWizard: state => {
    return state.hasSeenWizard
  },
  processes: (state, getters) => ({ bucketId, onlineOnly }) => {
    const bucket = getters.bucketById(bucketId)
    if (!bucket || !bucket.processes) return []

    return _(bucket.processes)
      .filter(proc => {
        return onlineOnly ? !isServerOffline(proc.server) : true
      })
      .value()
  },
  processesFromFilter: (state, getters) => ({ bucketId, filter }) => {
    if (!filter) return []
    if (filter.apps && filter.apps.length === 0 && filter.servers && filter.servers.length === 0) {
      return []
    }
    const bucket = getters.bucketById(bucketId)
    switch (filter.type) {
      case 'Apps': {
        const apps = filter.apps
        return bucket.processes.filter(proc => apps.includes(proc.name))
      }
      case 'Servers': {
        const servers = filter.servers
        return bucket.processes.filter(proc => servers.includes(proc.server))
      }
      case 'Apps&Servers': {
        if (!filter.apps || !filter.servers) return []
        const apps = filter.apps
        const servers = filter.servers
        return bucket.processes.filter(
          proc =>
            (servers.includes(proc.server) || servers.length === 0) &&
            (apps.includes(proc.name) || apps.length === 0),
        )
      }
      default:
        return null
    }
  },
  currentRouteRequireApp: state => {
    return Boolean(state.route && state.route.meta.requireApp)
  },
  currentAppName: createMemoizedGetter(
    () => ({
      maybeCurrentAppName: store.state.maybeCurrentAppName,
      knownAppNames: store.getters.select({ appName: false }).sortedAppNames,
    }),
    ({ maybeCurrentAppName, knownAppNames }) => {
      return maybeCurrentAppName && knownAppNames.includes(maybeCurrentAppName)
        ? maybeCurrentAppName
        : knownAppNames[0] || null
    },
  ),
  currentAppType(state) {
    return state.route.params.appType
  },
  currentAppQuery: (state, getters) => {
    const appName = state.route.query.app
    if (!appName) {
      return {
        initiator: getters.currentAppType === BACKEND_APP_TYPE ? 'node' : 'browser',
      }
    }

    if (getters.currentAppType === BACKEND_APP_TYPE) {
      return {
        app: appName,
      }
    }

    const frontApp = getters.frontendApps.find(app => app.name === appName)
    if (frontApp) {
      return {
        initiator: 'browser',
        source: frontApp.id,
      }
    }

    // Should not happen
    return {
      initiator: 'browser',
      source: 'not-found',
    }
  },

  cpuProfiles: (state, getters) => ({ bucketId }) => {
    return getters.bucketById(bucketId).ui.cpuProfilingPage.profiles
  },
  memSnapshots: (state, getters) => ({ bucketId }) => {
    return getters.bucketById(bucketId).ui.memProfilingPage.snapshots
  },
  profilings: (state, getters) => ({ bucketId }) => {
    return getters.bucketById(bucketId).profilings
  },
  tokens: state => {
    return state.tokens
  },
  km: state => {
    return state.km
  },
  otp: state => {
    return state.otp
  },
  areCriticalResourcesFetched: state => {
    return state.areCriticalResourcesFetched
  },
  isPageLoading: state => {
    return state.isPageLoading
  },
  authorizedUsers: (state, getters) => ({ bucketId = state.currentBucketId }) => {
    return getters.bucketById(bucketId).authorizedUsers
  },
  theme: state => {
    return state.theme
  },
  billing: (state, getters) => ({ bucketId = state.currentBucketId }) => {
    return getters.bucketById(bucketId).billing
  },
  billingMetadata: (state, getters) => ({ bucketId = state.currentBucketId }) => {
    return getters.bucketById(bucketId).billing.meta.metadata || {}
  },
  serversLinked: state => ({ bucketId = state.currentBucketId }) => {
    return state.buckets[bucketId].serversLinked
  },
  activatePM2: state => {
    return state.activatePM2
  },
  upgradeStepper: state => {
    return state.upgradeStepper
  },
  realtimeIssues: (state, getters) => ({
    bucketId = state.currentBucketId,
    appName,
    serverName,
  }) => {
    const bucket = getters.bucketById(bucketId)
    if (!bucket) return []

    return bucket.realtimeIssues.filter(
      issue =>
        (appName !== undefined ? issue.process && issue.process.name === appName : true) &&
        (serverName !== undefined ? issue.process && issue.process.server === serverName : true),
    )
  },
  alerts: (state, getters) => ({ bucketId }) => {
    return Object.values(getters.bucketById(bucketId).alerts)
  },
  anomalies: (state, getters) => ({ bucketId = state.route.params.id }) => {
    return getters.bucketById(bucketId).anomalies
  },
  userOptions: (state, getters) => ({ bucketId = state.route.params.id }) => {
    return (
      _.get(
        getters.bucketById(bucketId),
        `bucket.configuration.ui_options.user_options.${getters.user.id}`,
      ) || {}
    )
  },
  webchecks: (state, getters) => ({ bucketId = state.route.params.id }) => {
    return getters.bucketById(bucketId).webchecks
  },
  backofficeURLs(state) {
    const bucketId = store.state.currentBucketId
    const email = store.state.user.email
    const baseURL = String(process.env.BO_URL).replace(/\/+$/, '')
    return {
      bucket: bucketId ? `${baseURL}/#/bucket/${bucketId}` : undefined,
      user: `${baseURL}/#/user/${email}`,
    }
  },
  hasDuplicatedServer: state => {
    return state.hasDuplicatedServer
  },
}
